'use client'

import {
  createContext,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  useContext,
  useMemo,
} from 'react'

import { type VolumeBasedPriceV1 } from '@redteclab/api/clients/bully'

import {
  type OpenableControls,
  useOpenableControls,
} from '../../../generic-hooks'

import { useProductQuantitySelectionControls } from './useProductQuantitySelectionControls'

type ProductVolumePricingContextValue = {
  currentVariantUpid?: string
  openableControls: OpenableControls
  quantitySelectionControls: {
    quantity: number
    setQuantity: Dispatch<SetStateAction<number>>
  }
  volumeBasedPrices: VolumeBasedPriceV1[]
}

const ProductVolumePricingContext = createContext<
  ProductVolumePricingContextValue | undefined
>(undefined)

export const ProductVolumePricingContextProvider: FC<
  PropsWithChildren<{
    currentVariantUpid?: string
    volumeBasedPrices?: VolumeBasedPriceV1[]
  }>
> = ({ children, currentVariantUpid, volumeBasedPrices }) => {
  const openableControls = useOpenableControls()

  const quantitySelectionControls = useProductQuantitySelectionControls({
    currentVariantUpid,
  })

  const contextValue = useMemo(
    (): ProductVolumePricingContextValue => ({
      currentVariantUpid,
      openableControls,
      quantitySelectionControls,
      volumeBasedPrices: volumeBasedPrices ?? [],
    }),
    [
      currentVariantUpid,
      volumeBasedPrices,
      openableControls,
      quantitySelectionControls,
    ],
  )

  return (
    <ProductVolumePricingContext.Provider value={contextValue}>
      {children}
    </ProductVolumePricingContext.Provider>
  )
}

export const useProductVolumePricingContext =
  (): ProductVolumePricingContextValue => {
    const contextValue = useContext(ProductVolumePricingContext)

    if (contextValue === undefined) {
      throw new Error(
        'useProductVolumePricingContext must be used within a ProductVolumePricingContext',
      )
    }

    return contextValue
  }
